/**
 * The project's single timebase.
 *
 * A project holds N source videos, but the user edits *one* thing: a
 * sequence. So the editor lays every source end-to-end on one axis and
 * works in "project seconds" — the timeline, the playhead and the player
 * all speak that, and only the moment a frame is fetched does it get
 * translated back to "file X at second Y".
 *
 * Two modes over the same structure:
 *
 * - ``full`` — every video, in order, whole. Adding a video appends to
 *   the end, and playback runs straight through the joins.
 * - ``clips`` — only the marked ranges, in list order. Same axis, same
 *   player: what the user sees is the cut already made, as one video.
 *
 * Pure, no React — the playback hook and the timeline component both read
 * from here so they can't disagree about where a second lives.
 */

import { orderedFreezes, type ProjectClip, type ProjectVideo, type ToolProject } from './model'

export type TimelineMode = 'full' | 'clips'

/** One continuous stretch of one source video, placed on the timebase. */
export interface TimelineSegment {
  /** Which {@link ProjectVideo} the frames come from. */
  videoId: string
  /** Bounds inside the source file. Equal on a freeze — a held frame
   * consumes no footage. */
  sourceStart: number
  sourceEnd: number
  /** Where the stretch begins on the project timeline. */
  start: number
  /**
   * Where it ends on the project timeline, precomputed — every lookup needs
   * it and floating-point re-derivation drifts.
   *
   * It used to be ``start + (sourceEnd - sourceStart)``. It is now
   * ``start + (sourceEnd - sourceStart) / speed`` for footage and
   * ``start + <the freeze's own length>`` for a held frame: project time and
   * source time are no longer the same clock, which is the single change
   * that both the speed and the freeze features rest on.
   */
  end: number
  /** Set in ``clips`` mode: which clip this stretch belongs to. ``null`` in
   * ``full`` mode, where segments are whole videos. */
  clipId: number | null
  /** Rate the footage plays at. Always 1 in ``full`` mode and on a freeze. */
  speed: number
  /**
   * Set when this stretch is a **held frame** rather than moving footage:
   * the id of the {@link FreezeFrame} it came from. The player pauses and
   * counts wall-clock instead of playing; the timeline hatches it; the
   * export loops a single extracted frame over silence.
   */
  freezeId: string | null
}

/** True when the stretch holds one frame instead of playing footage. */
export function isFreeze(segment: TimelineSegment): boolean {
  return segment.freezeId !== null
}

export interface VirtualTimeline {
  mode: TimelineMode
  segments: TimelineSegment[]
  /** Total length in project seconds. */
  duration: number
}

/** A point on the timebase, resolved to the frame that renders it. */
export interface ResolvedPoint {
  segment: TimelineSegment
  index: number
  /** Position inside the source file — what goes on ``currentTime``. */
  sourceTime: number
}

const EMPTY: VirtualTimeline = { mode: 'full', segments: [], duration: 0 }

/**
 * Lay the project out on one axis.
 *
 * Zero-length pieces are dropped rather than kept as instants: a video
 * whose duration the browser hasn't reported yet is 0 (the imports
 * endpoint doesn't probe), and a segment of length 0 would be an
 * unreachable point that every lookup has to special-case. It reappears
 * on its own once ``onDuration`` fills the value in.
 *
 * **Effects only exist in ``clips``.** ``full`` is the raw footage — that
 * is what "Vídeos completos" claims to show, and speeding it up or holding
 * a frame there would make the one view you can trust as a reference lie.
 * Hidden clips drop out here too, which is the whole of that feature on the
 * playback side.
 */
export function buildTimeline(project: ToolProject, mode: TimelineMode): VirtualTimeline {
  const segments: TimelineSegment[] = []
  let cursor = 0

  if (mode === 'clips') {
    for (const clip of project.clips) {
      if (clip.hidden) continue
      if (clip.end - clip.start <= 0) continue
      const speed = clip.speed > 0 ? clip.speed : 1

      // A clip with freezes is several stretches: footage, held frame,
      // footage, … The split is what lets the player pause on the frame and
      // the export loop it, without either of them knowing about clips.
      let source = clip.start
      for (const freeze of orderedFreezes(clip)) {
        const at = Math.min(clip.end, Math.max(source, freeze.at))
        if (at > source) {
          const length = (at - source) / speed
          segments.push({
            videoId: clip.videoId,
            sourceStart: source,
            sourceEnd: at,
            start: cursor,
            end: cursor + length,
            clipId: clip.id,
            speed,
            freezeId: null,
          })
          cursor += length
          source = at
        }
        segments.push({
          videoId: clip.videoId,
          sourceStart: at,
          sourceEnd: at,
          start: cursor,
          end: cursor + freeze.duration,
          clipId: clip.id,
          // A held frame has no rate — 1 keeps every division safe.
          speed: 1,
          freezeId: freeze.id,
        })
        cursor += freeze.duration
      }

      if (clip.end > source) {
        const length = (clip.end - source) / speed
        segments.push({
          videoId: clip.videoId,
          sourceStart: source,
          sourceEnd: clip.end,
          start: cursor,
          end: cursor + length,
          clipId: clip.id,
          speed,
          freezeId: null,
        })
        cursor += length
      }
    }
  } else {
    for (const video of project.videos) {
      if (video.duration <= 0) continue
      segments.push({
        videoId: video.id,
        sourceStart: 0,
        sourceEnd: video.duration,
        start: cursor,
        end: cursor + video.duration,
        clipId: null,
        speed: 1,
        freezeId: null,
      })
      cursor += video.duration
    }
  }

  return { mode, segments, duration: cursor }
}

/** Empty timeline — for the render before a project is loaded. */
export function emptyTimeline(): VirtualTimeline {
  return EMPTY
}

/**
 * Which frame renders project-second ``time``.
 *
 * Boundaries belong to the segment that *starts* there, so scrubbing to a
 * join shows the first frame of what comes next rather than the last
 * frame of what just ended — that's the frame the user is looking for.
 * Past the end, the last segment's final frame; ``null`` only when there
 * is nothing to show at all.
 */
export function resolvePoint(timeline: VirtualTimeline, time: number): ResolvedPoint | null {
  const { segments } = timeline
  if (segments.length === 0) return null

  const clamped = Math.max(0, Math.min(timeline.duration, time))
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i]
    if (!segment) continue
    if (clamped < segment.end || i === segments.length - 1) {
      const offset = Math.max(0, clamped - segment.start)
      return {
        segment,
        index: i,
        // A held frame renders one source instant however long it lasts;
        // footage advances at the segment's own rate.
        sourceTime: segment.freezeId
          ? segment.sourceStart
          : Math.min(segment.sourceEnd, segment.sourceStart + offset * segment.speed),
      }
    }
  }
  return null
}

/**
 * Inverse of {@link resolvePoint}: where a position inside a source file
 * lands on the timebase. Used to drive the playhead from the ``<video>``
 * element's own ``currentTime``.
 *
 * ``segmentIndex`` disambiguates — the same source second can appear
 * more than once (a video used by two clips, or the same file added
 * twice), and without it the playhead would jump to the first match.
 */
export function timeOfSource(
  timeline: VirtualTimeline,
  segmentIndex: number,
  sourceTime: number,
): number | null {
  const segment = timeline.segments[segmentIndex]
  if (!segment) return null
  // A freeze maps a whole span of project time onto one source instant, so
  // the inverse doesn't exist. Its own start is the only honest answer — the
  // player counts a freeze's progress itself rather than reading it off the
  // element, which is paused throughout.
  if (segment.freezeId) return segment.start
  const offset = Math.max(0, Math.min(segment.sourceEnd - segment.sourceStart, sourceTime - segment.sourceStart))
  return segment.start + offset / segment.speed
}

/**
 * Where a clip sits on the timebase, or ``null`` when it isn't on it.
 *
 * In ``full`` mode a clip is a mark inside its video's stretch; in
 * ``clips`` mode the clip *is* a segment. The timeline component draws
 * clip bars from this, so it needs no mode-specific branch.
 */
export function clipBounds(
  timeline: VirtualTimeline,
  clip: ProjectClip,
): { start: number; end: number } | null {
  if (timeline.mode === 'clips') {
    // A clip is several stretches once it holds a freeze, so the bar spans
    // from the first to the last — which is exactly the "the freeze makes
    // the clip longer" the design asked for, with no separate block.
    let start: number | null = null
    let end: number | null = null
    for (const segment of timeline.segments) {
      if (segment.clipId !== clip.id) continue
      if (start === null) start = segment.start
      end = segment.end
    }
    return start !== null && end !== null ? { start, end } : null
  }
  const segment = timeline.segments.find((s) => s.videoId === clip.videoId)
  if (!segment) return null
  // A clip is always inside its own video, so the offset is direct.
  return {
    start: segment.start + clip.start,
    end: segment.start + clip.end,
  }
}

/**
 * Where a clip's held frames sit on the timebase, for the hatched overlay.
 *
 * Empty in ``full`` mode: there the clip bar marks source footage, and a
 * freeze occupies no source at all — drawing it would claim footage is
 * frozen that in fact plays. It shows up in "só os cortes", which is where
 * the cut (and the freeze) actually exists.
 */
export function freezeBounds(
  timeline: VirtualTimeline,
  clip: ProjectClip,
): { id: string; start: number; end: number }[] {
  if (timeline.mode !== 'clips') return []
  const out: { id: string; start: number; end: number }[] = []
  for (const segment of timeline.segments) {
    if (segment.clipId !== clip.id || !segment.freezeId) continue
    out.push({ id: segment.freezeId, start: segment.start, end: segment.end })
  }
  return out
}

/**
 * Convert a project-second into "video X at second Y" for a caller that
 * still thinks per-video — the marker drag and the "new clip" flow, which
 * write ``ProjectClip`` bounds in source coordinates.
 */
export function toSourceTime(
  timeline: VirtualTimeline,
  time: number,
): { videoId: string; sourceTime: number } | null {
  const point = resolvePoint(timeline, time)
  if (!point) return null
  return { videoId: point.segment.videoId, sourceTime: point.sourceTime }
}

/** Start of a video's stretch on the timebase — the offset a per-video
 * coordinate needs to become a project one. ``null`` when the video isn't
 * laid out (unknown duration, or ``clips`` mode with no clip on it). */
export function videoOffset(timeline: VirtualTimeline, videoId: string): number | null {
  const segment = timeline.segments.find((s) => s.videoId === videoId)
  return segment ? segment.start : null
}

/**
 * Translate a moment between the two axes a project has.
 *
 * Captions are stored on the **full** axis — the stable one. The clips
 * axis reflows every time a clip's bounds change, so anchoring to it would
 * silently move every caption whenever you trimmed something. But the
 * exported video *is* the clips axis, and so is what the player shows in
 * "só os cortes", hence this translation.
 *
 * Both directions pass through the source file's own clock, which is the
 * only thing the two axes agree on.
 *
 * Returns ``null`` when the moment has no counterpart: a caption placed
 * over footage that no clip keeps simply isn't in the export, which is the
 * right answer rather than an error.
 */
export function toClipsTime(
  full: VirtualTimeline,
  clips: VirtualTimeline,
  time: number,
): number | null {
  const point = resolvePoint(full, time)
  if (!point) return null
  const { videoId, sourceTime } = { videoId: point.segment.videoId, sourceTime: point.sourceTime }
  for (const segment of clips.segments) {
    // Held frames are skipped: they cover a single source instant, so every
    // moment they could claim is already claimed by the footage around them.
    if (segment.videoId !== videoId || segment.freezeId) continue
    if (sourceTime < segment.sourceStart || sourceTime >= segment.sourceEnd) continue
    return segment.start + (sourceTime - segment.sourceStart) / segment.speed
  }
  return null
}

/** Inverse of {@link toClipsTime}. */
export function toFullTime(
  clips: VirtualTimeline,
  full: VirtualTimeline,
  time: number,
): number | null {
  const point = resolvePoint(clips, time)
  if (!point) return null
  const offset = videoOffset(full, point.segment.videoId)
  if (offset === null) return null
  return offset + point.sourceTime
}

/**
 * Videos the server never described — the editor preloads their metadata so
 * the browser can fill the gaps.
 *
 * Two gaps, and both are load-bearing. Without a **duration** the
 * concatenated timeline can't lay the video out, so a project reopened from
 * "já no servidor" would show only the videos that happened to have been
 * played before. Without **dimensions** the stage has no aspect to give the
 * frame: it collapses to nothing, and captions — which are fractions of the
 * frame — have nothing meaningful to measure against.
 */
export function videosNeedingProbe(videos: ProjectVideo[]): ProjectVideo[] {
  return videos.filter((v) => v.duration <= 0 || !v.width || !v.height)
}
